import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Slider,
    Button,
    List,
    ListItem,
    ListItemText,
    Chip,
    IconButton,
    Stack,
    Alert,
    CircularProgress,
    Divider,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup,
    Backdrop,
    Tooltip,
    InputAdornment
} from '@mui/material';
import {
    Upload as UploadIcon,
    Delete as DeleteIcon,
    Send as SendIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppStateContext } from '../../state/AppContextProvider';
import { HttpServiceContext } from '../../state/HttpProvider';
import { PromptConfig } from '../../common/models';
import { ChatService } from '../../services/chatService';
import LoadingSpinner from '../common/LoadingSpinner';

interface PlaygroundFile {
    file: File;
    id: string;
}

interface PlaygroundMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

interface PlaygroundRequest {
    promptId: string;
    temperature: number;
    documents: File[];
    userInput: string;
    conversationHistory: PlaygroundMessage[];
}

const MAX_CONVERSATION_TURNS = 10;
const MAX_FILES = 5;

const MODEL_OPTIONS = [
    { value: 'gpt4o', label: 'GPT-4o' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-5-chat', label: 'GPT-5 Chat' },
    { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
    { value: 'gpt-5-mini', label: 'GPT-5 Mini' }
];

const Playground: React.FC = () => {
    const { prompts,
        savePrompt,
        updatePrompt,
        deletePrompt,
        fetchPrompts,
    } = useAppStateContext();
    const { httpService: apiService } = useContext(HttpServiceContext);
    const chatService = useMemo(() => new ChatService(apiService), [apiService]);

    // Form state
    const [selectedPromptId, setSelectedPromptId] = useState<string>('');
    const [customPrompt, setCustomPrompt] = useState<string>('');
    const [playgroundTitle, setPlaygroundTitle] = useState<string>('');
    const [temperature, setTemperature] = useState<number>(0.7);
    const [selectedModel, setSelectedModel] = useState<string>('gpt4o');
    const [enableWebSearch, setEnableWebSearch] = useState<boolean>(false);
    const [userInput, setUserInput] = useState<string>('');
    const [uploadedFiles, setUploadedFiles] = useState<PlaygroundFile[]>([]);
    const [storedDocumentId, setStoredDocumentId] = useState<string | null>(null); // Kernel Memory ID from stored docs
    const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingPrompts, setIsLoadingPrompts] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<string>('');

    // Conversation history state
    const [conversationHistory, setConversationHistory] = useState<PlaygroundMessage[]>([]);
    const [conversationDisplay, setConversationDisplay] = useState<PlaygroundMessage[]>([]);
    const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);
    // Expanded chat state
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggleExpand = () => setIsExpanded(prev => !prev);
    // Expanded configuration state
    const [isConfigExpanded, setIsConfigExpanded] = useState<boolean>(false);
    const toggleConfigExpand = () => setIsConfigExpanded(prev => !prev);

    // Ref for auto-scrolling to bottom
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [conversationDisplay, response]);

    // Prompts are loaded centrally via AppContext/PromptManager.
    // Playground should not auto-fetch prompts on mount to avoid duplicate/aborted requests.

    // Handle file upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles: PlaygroundFile[] = Array.from(files).map(file => ({
                file,
                id: Math.random().toString(36).substr(2, 9)
            }));

            // Check if adding new files would exceed the limit
            const totalFiles = uploadedFiles.length + newFiles.length;
            if (totalFiles > MAX_FILES) {
                setError(`Maximum ${MAX_FILES} files allowed. Please remove some files before uploading more.`);
                event.target.value = '';
                return;
            }

            setUploadedFiles(prev => [...prev, ...newFiles]);
            setError(null); // Clear any previous file limit errors
        }
        // Reset input value to allow uploading the same file again
        event.target.value = '';
    };

    // Remove uploaded file
    const removeFile = (fileId: string) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // Clear all files
    const clearAllFiles = () => {
        setUploadedFiles([]);
    };

    // Manually refresh prompts
    const handleRefreshPrompts = () => {
        setIsLoadingPrompts(true);
        const controller = new AbortController();

        fetchPrompts(controller.signal)
            .catch((error: any) => {
                if (error.name !== 'AbortError') {
                    console.error('Failed to refresh prompts:', error);
                }
            })
            .finally(() => {
                setIsLoadingPrompts(false);
            });
    };

    // Format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Handle playground chat submission
    const handleSubmit = async () => {
        if ((!selectedPromptId && !customPrompt.trim()) || !userInput.trim()) {
            setError('Please select a prompt or enter a custom prompt, and enter your message');
            return;
        }

        // Check file limit
        if (uploadedFiles.length > MAX_FILES) {
            setError(`Maximum ${MAX_FILES} files allowed. Please remove some files before sending.`);
            return;
        }

        // Add current user message to history
        const currentUserMessage: PlaygroundMessage = {
            role: 'user',
            content: userInput,
            timestamp: new Date()
        };

        // Limit conversation history to MAX_CONVERSATION_TURNS
        const updatedHistory = [...conversationHistory, currentUserMessage];
        if (updatedHistory.length > MAX_CONVERSATION_TURNS * 2) { // *2 because each turn has user + assistant
            updatedHistory.splice(0, 2); // Remove oldest user-assistant pair
        }

        // Update display with user message
        const updatedDisplay = [...conversationDisplay, currentUserMessage];
        setConversationDisplay(updatedDisplay);

        setIsLoading(true);
        setIsWaitingForResponse(true);
        setError(null);
        setResponse('');
        const currentInput = userInput;
        setUserInput(''); // Clear input immediately

        try {
            // Create form data for the request
            const formData = new FormData();

            // Always use the current configuration values, not the stored prompt
            // This allows users to edit and test prompts without saving
            formData.append('temperature', temperature.toString());
            formData.append('modelId', selectedModel);
            formData.append('enableWebSearch', enableWebSearch.toString());
            formData.append('userInput', currentInput);
            formData.append('conversationHistory', JSON.stringify(updatedHistory));

            // Always send customPrompt with current value from text area
            if (customPrompt.trim()) {
                formData.append('customPrompt', customPrompt);
            }

            // Add files to form data
            uploadedFiles.forEach(fileItem => {
                formData.append('documents', fileItem.file);
            });
            
            // Add stored documentId if available (from prompt's associated docs)
            if (storedDocumentId) {
                formData.append('storedDocumentId', storedDocumentId);
            }

            // Call the playground chat API endpoint
            const response = await chatService.playgroundChatApiEndpoint(formData, new AbortController().signal);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Handle streaming response
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let accumulatedResponse = '';

            if (reader) {
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        accumulatedResponse += chunk;
                        setResponse(accumulatedResponse);

                        // Hide waiting spinner on first chunk
                        if (accumulatedResponse.length > 0) {
                            setIsWaitingForResponse(false);
                        }
                    }
                } finally {
                    reader.releaseLock();
                }
            }

            // Add assistant response to conversation history
            const assistantMessage: PlaygroundMessage = {
                role: 'assistant',
                content: accumulatedResponse,
                timestamp: new Date()
            };

            const finalHistory = [...updatedHistory, assistantMessage];
            setConversationHistory(finalHistory);

            // Update display with assistant message
            const finalDisplay = [...updatedDisplay, assistantMessage];
            setConversationDisplay(finalDisplay);

            // Clear the streaming response since it's now in conversation display
            setResponse('');
            setIsLoading(false);

        } catch (err: any) {
            setError(err.message || 'An error occurred while processing your request');
            setIsLoading(false);
            setIsWaitingForResponse(false);
        }
    };

    // Clear the conversation
    const clearConversation = () => {
        setUserInput('');
        setResponse('');
        setError(null);
        setConversationHistory([]);
        setConversationDisplay([]);
        setIsWaitingForResponse(false);
        // Don't clear customPrompt, selectedPromptId, or enableWebSearch
    };

    // Get selected prompt details
    const selectedPrompt = prompts?.find(p => p.id === selectedPromptId);

    // Sync playgroundTitle and customPrompt with selectedPrompt
    useEffect(() => {
        if (selectedPrompt) {
            // Always populate title and prompt content to display them
            setPlaygroundTitle(selectedPrompt.title || '');
            setCustomPrompt(selectedPrompt.prompt || '');
            
            // Sync temperature if available
            if (selectedPrompt.temp) {
                const tempValue = parseFloat(selectedPrompt.temp);
                if (!isNaN(tempValue)) {
                    setTemperature(tempValue);
                }
            }
            
            // Pre-load stored documentId if available, otherwise load from blob URLs
            if (selectedPrompt.playgroundDocumentId) {
                // Use existing Kernel Memory document ID
                setStoredDocumentId(selectedPrompt.playgroundDocumentId);
                console.log('Using stored documentId:', selectedPrompt.playgroundDocumentId);
            } else if (selectedPrompt.playgroundDocuments && selectedPrompt.playgroundDocuments.length > 0) {
                // Convert blob URLs to Kernel Memory document ID
                const loadDocuments = async () => {
                    try {
                        setIsLoadingDocs(true);
                        const result = await apiService.preloadPlaygroundDocuments(selectedPrompt.playgroundDocuments!);
                        setStoredDocumentId(result.documentId);
                        console.log(`Pre-loaded ${result.processedCount} documents with documentId:`, result.documentId);
                    } catch (error) {
                        console.error('Failed to pre-load documents:', error);
                        setError('Failed to load associated documents');
                    } finally {
                        setIsLoadingDocs(false);
                    }
                };
                loadDocuments();
            } else {
                setStoredDocumentId(null);
            }
        } else if (!selectedPromptId) {
            // Clear title and stored docs when no prompt is selected
            setPlaygroundTitle('');
            setStoredDocumentId(null);
        }
    }, [selectedPrompt, selectedPromptId, apiService]);

    // Auto-select model when prompt is chosen
    useEffect(() => {
        if (selectedPrompt && selectedPrompt.llmModel) {
            setSelectedModel(selectedPrompt.llmModel);
        }
    }, [selectedPrompt]);

    // Filter prompts based on search functionality
    // Note: Server filters prompts by role (Developer sees all, Playground users see only their own)
    const getFilteredPrompts = () => {
        if (!prompts) return [];

        return prompts.map(prompt => ({
            ...prompt,
            searchText: `${prompt.agent} ${prompt.title} v${prompt.version} ${prompt.prompt || ''}`.toLowerCase()
        }));
    };

    const filteredPrompts = getFilteredPrompts();

    // Styles for right panel when expanded vs normal
    const rightPanelSx = isExpanded
        ? {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: (theme: any) => theme.zIndex.modal + 2,
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column' as const,
            backgroundColor: 'background.default'
        }
        : {
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column' as const
        };

    return (
        <Box sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 1, sm: 2, md: 3 },
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            <Typography variant="h4" gutterBottom sx={{ flexShrink: 0 }}>
                AI Playground
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ flexShrink: 0 }}>
                Experiment with different prompts, upload documents for RAG, and adjust temperature settings
            </Typography>

            <Box sx={{
                display: 'flex',
                gap: { xs: 2, md: 3 },
                flexGrow: 1,
                flexShrink: 1,
                mt: 1,
                flexDirection: { xs: 'column', lg: 'row' },
                minHeight: 0,
                overflow: 'hidden'
            }}>
                {/* Left Panel - Configuration */}
                <Box sx={isConfigExpanded ? {
                    width: '50%',
                    flexShrink: 0,
                    height: '100%',
                    minHeight: 0,
                    display: 'flex'
                } : {
                    width: { xs: '100%', lg: '300px', xl: '350px' },
                    flexShrink: 0,
                    height: { xs: 'auto', lg: '100%' },
                    maxHeight: { xs: '50vh', lg: '100%' },
                    minHeight: 0,
                    display: isExpanded ? 'none' : 'flex'
                }}>
                    <Paper sx={{
                        p: { xs: 2, md: 3 },
                        width: '100%',
                        height: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6">
                                Experiment Here
                            </Typography>
                            <Tooltip title={isConfigExpanded ? 'Collapse configuration' : 'Expand configuration'}>
                                <IconButton
                                    size="small"
                                    onClick={toggleConfigExpand}
                                    color="primary"
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        boxShadow: 1,
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    {isConfigExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Scrollable Content Area */}
                        <Box sx={{ flexGrow: 1, overflow: 'auto', pr: 1 }}>

                            {/* Prompt Selection with Search */}
                            <Box sx={{ mb: 3 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2">
                                        Either Select Prompt {isLoadingPrompts && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={handleRefreshPrompts}
                                        disabled={isLoadingPrompts}
                                        color="primary"
                                        title="Refresh Prompts"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                                {isLoadingPrompts ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Loading prompts...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Autocomplete
                                        options={filteredPrompts}
                                        value={filteredPrompts.find(p => p.id === selectedPromptId) || null}
                                        onChange={(_, value) => {
                                            setSelectedPromptId(value?.id || '');
                                            // Clear customPrompt when clearing selection
                                            if (!value) {
                                                setCustomPrompt('');
                                            }
                                            // useEffect will sync customPrompt and playgroundTitle with selected prompt
                                        }}
                                        sx={{
                                            '& .MuiAutocomplete-clearIndicator': {
                                                color: '#d32f2f',
                                                opacity: 0.9,
                                                '&:hover': {
                                                    color: '#b71c1c',
                                                    opacity: 1
                                                }
                                            },
                                            '& .MuiAutocomplete-popupIndicator': {
                                                color: '#d32f2f',
                                                opacity: 0.9,
                                                '&:hover': {
                                                    color: '#b71c1c',
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                        getOptionLabel={(option) => `${option.title} (v${option.version}) - ${option.agent}`}
                                        renderOption={(props, option) => {
                                            const { key, ...otherProps } = props;
                                            return (
                                                <Box component="li" key={key} {...otherProps}>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {option.title} (v{option.version})
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.agent}
                                                            {option.isActive && (
                                                                <Chip
                                                                    label="Active"
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Search by agent, title, version, or content..."
                                                variant="outlined"
                                                fullWidth
                                            />
                                        )}
                                        filterOptions={(options, { inputValue }) => {
                                            const searchTerm = inputValue.toLowerCase();
                                            return options.filter(option =>
                                                option.searchText.includes(searchTerm)
                                            );
                                        }}
                                        noOptionsText={(!prompts || prompts.length === 0) ? "No prompts available" : "No prompts found"}
                                    />
                                )}
                            </Box>

                            {/* Show selected prompt details */}
                            {selectedPrompt && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Selected Prompt Details
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Agent:</strong> {selectedPrompt.agent}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Title:</strong> {selectedPrompt.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Version:</strong> {selectedPrompt.version}
                                    </Typography>
                                    {selectedPrompt.playgroundDocuments && selectedPrompt.playgroundDocuments.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                                                Shared Documents ({selectedPrompt.playgroundDocuments.length}) - All versions of "{selectedPrompt.title}":
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {selectedPrompt.playgroundDocuments.map((docUrl, idx) => {
                                                    const fileName = docUrl.split('/').pop() || 'document';
                                                    return (
                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>📎</span> {fileName}
                                                            </Typography>
                                                            {selectedPrompt.playgroundCreation && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        color: 'error.main',
                                                                        fontSize: '0.75rem',
                                                                        '&:hover': { opacity: 0.7 }
                                                                    }}
                                                                    onClick={async () => {
                                                                        if (window.confirm(`Remove "${fileName}"? This will affect all versions of this prompt if no other version references it.`)) {
                                                                            try {
                                                                                await apiService.deletePlaygroundDocuments(selectedPrompt.title, [docUrl]);
                                                                                // Update the prompt to remove this document reference
                                                                                const updatedDocs = selectedPrompt.playgroundDocuments!.filter(url => url !== docUrl);
                                                                                const updatedPrompt: PromptConfig = {
                                                                                    ...selectedPrompt,
                                                                                    playgroundDocuments: updatedDocs.length > 0 ? updatedDocs : undefined,
                                                                                };
                                                                                const controller = new AbortController();
                                                                                await apiService.updatePrompt(updatedPrompt, controller.signal);
                                                                                await fetchPrompts(controller.signal);
                                                                            } catch (err: any) {
                                                                                console.error('Failed to delete document:', err);
                                                                                setError('Failed to delete document: ' + err.message);
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    ✕
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Custom Prompt Input + Save + Title */}
                            <Box sx={{ mb: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Or Create Custom Prompt
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: 'background.paper',
                                            borderRadius: '40px',
                                            boxShadow: 'none',
                                            height: '32px',
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            cursor: isLoadingPrompts || !customPrompt.trim() || !playgroundTitle.trim() || (selectedPrompt && !selectedPrompt.playgroundCreation) ? 'default' : 'pointer',
                                            px: 0,
                                            py: 0,
                                            border: '1.5px solid #e50c20',
                                            transition: 'background 0.2s, color 0.2s',
                                            opacity: isLoadingPrompts || !customPrompt.trim() || !playgroundTitle.trim() || (selectedPrompt && !selectedPrompt.playgroundCreation) ? 0.5 : 1,
                                            ...(!(isLoadingPrompts || !customPrompt.trim() || !playgroundTitle.trim() || (selectedPrompt && !selectedPrompt.playgroundCreation)) && {
                                                '&:hover': {
                                                    bgcolor: '#e50c20',
                                                    '& .save-btn-text': {
                                                        color: '#fff',
                                                    },
                                                },
                                            }),
                                        }}
                                        onClick={async () => {
                                            if (!(isLoadingPrompts || !customPrompt.trim() || !playgroundTitle.trim() || (selectedPrompt && !selectedPrompt.playgroundCreation))) {
                                                // Always create a new prompt with a new version (immutable versioning)
                                                const controller = new AbortController();

                                                // Generate new version
                                                const generatedVersion = `v${new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14)}`;

                                                setIsLoadingPrompts(true);
                                                try {
                                                    // Handle documents FIRST: either newly uploaded files OR existing blob URLs
                                                    let finalDocumentUrls: string[] | undefined;
                                                    let finalDocumentId: string | undefined;
                                                    
                                                    // Check if there are new files uploaded in this session
                                                    if (uploadedFiles.length > 0) {
                                                        const files = uploadedFiles.map(pf => pf.file);
                                                        const title = playgroundTitle.trim() || 'Untitled';
                                                        const result = await apiService.uploadPlaygroundDocuments(title, files);
                                                        finalDocumentUrls = result.documentUrls;
                                                        
                                                        // Get Kernel Memory document ID for immediate use
                                                        const preloadResult = await apiService.preloadPlaygroundDocuments(result.documentUrls);
                                                        finalDocumentId = preloadResult.documentId;
                                                        setStoredDocumentId(preloadResult.documentId);
                                                    } else if (selectedPrompt?.playgroundDocuments && selectedPrompt.playgroundDocuments.length > 0) {
                                                        // No new files, but existing prompt has documents - carry them forward
                                                        finalDocumentUrls = selectedPrompt.playgroundDocuments;
                                                        // Preload existing documents into Kernel Memory for the new version
                                                        const preloadResult = await apiService.preloadPlaygroundDocuments(selectedPrompt.playgroundDocuments);
                                                        finalDocumentId = preloadResult.documentId;
                                                        setStoredDocumentId(preloadResult.documentId);
                                                    }
                                                    
                                                    // Build PromptConfig with documents included from the start
                                                    const promptObj: PromptConfig = {
                                                        agent: 'Playground',
                                                        title: playgroundTitle.trim() || 'Untitled',
                                                        prompt: customPrompt.trim() || '',
                                                        version: generatedVersion,
                                                        temp: temperature.toString(),
                                                        llmModel: selectedModel || undefined,
                                                        isActive: false,
                                                        playgroundCreation: true,
                                                        playgroundDocuments: finalDocumentUrls,
                                                        playgroundDocumentId: finalDocumentId,
                                                    } as PromptConfig;
                                                    
                                                    // Save the prompt with documents already included
                                                    await apiService.savePrompt(promptObj, controller.signal);
                                                    
                                                    // Refresh prompts via context helper
                                                    await fetchPrompts(controller.signal);
                                                    setError(null);
                                                } catch (err: any) {
                                                    if (err.name !== 'AbortError') {
                                                        setError(err.message || 'Failed to save prompt');
                                                    }
                                                } finally {
                                                    setIsLoadingPrompts(false);
                                                }
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                background: '#e50c20',
                                                width: '2%',
                                                minWidth: '20px',
                                                height: '100%',
                                                borderRadius: '40px 0 0 40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        />
                                        <Typography
                                            className="save-btn-text"
                                            component="span"
                                            sx={{
                                                fontSize: '.85rem',
                                                fontWeight: 600,
                                                pl: 1.5,
                                                pr: 2,
                                                py: 0.5,
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                color: 'text.primary',
                                                userSelect: 'none',
                                                transition: 'color 0.2s',
                                            }}
                                        >
                                            {selectedPromptId ? 'Update' : 'Save'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <TextField
                                    fullWidth
                                    required
                                    label="Prompt Title"
                                    value={playgroundTitle}
                                    onChange={e => setPlaygroundTitle(e.target.value)}
                                    sx={{ mb: 1 }}
                                    helperText={
                                        selectedPrompt && !selectedPrompt.playgroundCreation
                                            ? "Cannot edit system prompts - clear selection to create new"
                                            : selectedPromptId
                                                ? "Edit to create a new version or save as new prompt"
                                                : "Title for this prompt (required)"
                                    }
                                    disabled={selectedPrompt && !selectedPrompt.playgroundCreation}
                                />
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Paste your custom prompt here (optional)..."
                                    value={customPrompt}
                                    onChange={(e) => {
                                        setCustomPrompt(e.target.value);
                                    }}
                                    helperText={
                                        selectedPrompt && !selectedPrompt.playgroundCreation
                                            ? "System prompts are read-only - you can use them for chat"
                                            : selectedPromptId
                                                ? "Edit the prompt content to update or create new version"
                                                : "Enter prompt content or select a prompt above"
                                    }
                                    disabled={selectedPrompt && !selectedPrompt.playgroundCreation}
                                />
                                {/* Version Dropdown for Playground prompts */}
                                {selectedPrompt && selectedPrompt.playgroundCreation && (
                                    <FormControl fullWidth sx={{ mt: 1 }}>
                                        <InputLabel>Version</InputLabel>
                                        <Select
                                            value={selectedPrompt.version}
                                            label="Version"
                                            onChange={e => {
                                                const v = e.target.value;
                                                // Find prompt by matching title, agent, and version
                                                const match = prompts?.find(p =>
                                                    p.title === selectedPrompt.title &&
                                                    p.agent === selectedPrompt.agent &&
                                                    p.version === v &&
                                                    p.playgroundCreation
                                                );
                                                if (match && match.id) setSelectedPromptId(match.id);
                                            }}
                                        >
                                            {prompts
                                                ?.filter(p =>
                                                    p.title === selectedPrompt.title &&
                                                    p.agent === selectedPrompt.agent &&
                                                    p.playgroundCreation
                                                )
                                                .sort((a, b) => b.version.localeCompare(a.version)) // Sort versions descending
                                                .map(p => (
                                                    <MenuItem key={p.id || p.version} value={p.version}>
                                                        {p.version}
                                                        {p.modifiedDate && ` - ${new Date(p.modifiedDate).toLocaleString()}`}
                                                    </MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>

                            {/* Conversation Context Indicator */}
                            {(conversationHistory.length > 0 || enableWebSearch) && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                                    <Typography variant="subtitle2" color="info.dark" gutterBottom>
                                        📝 Active Features
                                    </Typography>
                                    {conversationHistory.length > 0 && (
                                        <Typography variant="body2" color="info.dark">
                                            {Math.ceil(conversationHistory.length / 2)} conversation turns • Context maintained for multi-turn conversations
                                        </Typography>
                                    )}
                                    {enableWebSearch && (
                                        <Typography variant="body2" color="info.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <SearchIcon fontSize="small" />
                                            Web search enabled • Agent can search the internet for current information
                                        </Typography>
                                    )}
                                    {conversationHistory.length > 0 && (
                                        <Typography variant="caption" color="info.dark">
                                            Maximum {MAX_CONVERSATION_TURNS} turns kept • Click "Clear" to reset
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Temperature Control */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Temperature: {temperature}
                                </Typography>
                                <Slider
                                    value={temperature}
                                    onChange={(_, value) => setTemperature(value as number)}
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 0.3, label: '0.3' },
                                        { value: 0.7, label: '0.7' },
                                        { value: 1, label: '1' }
                                    ]}
                                    valueLabelDisplay="auto"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Lower values make output more focused, higher values more creative
                                </Typography>
                            </Box>

                            {/* File Upload */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Document Upload (RAG)
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Upload up to {MAX_FILES} documents for context-aware responses
                                </Typography>


                                <input
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    id="file-upload-input"
                                    accept=".pdf,.txt,.doc,.docx,.md"
                                />
                                <label htmlFor="file-upload-input">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon />}
                                        fullWidth
                                        disabled={uploadedFiles.length >= MAX_FILES}
                                        sx={{ mb: 2 }}
                                    >
                                        {uploadedFiles.length >= MAX_FILES
                                            ? `Maximum ${MAX_FILES} files reached`
                                            : `Upload Documents (${uploadedFiles.length}/${MAX_FILES})`}
                                    </Button>
                                </label>

                                {/* Uploaded Files List */}
                                {uploadedFiles.length > 0 && (
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="caption" color={uploadedFiles.length > MAX_FILES ? "error" : "text.secondary"}>
                                                {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded ({uploadedFiles.length}/{MAX_FILES})
                                                {uploadedFiles.length > MAX_FILES && ' - Too many files!'}
                                            </Typography>
                                            <IconButton size="small" onClick={clearAllFiles} color="error">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                        <List dense>
                                            {uploadedFiles.map((fileItem) => (
                                                <ListItem
                                                    key={fileItem.id}
                                                    sx={{
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        py: 0.5
                                                    }}
                                                    secondaryAction={
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            onClick={() => removeFile(fileItem.id)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" noWrap>
                                                                {fileItem.file.name}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatFileSize(fileItem.file.size)}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                {/* Right Panel - Chat Interface */}
                <Box sx={rightPanelSx}>
                    <Paper sx={{
                        p: { xs: 2, md: 3 },
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                    }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
                            <Typography variant="h6">
                                Chat Interface
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Model</InputLabel>
                                        <Select
                                            value={selectedModel}
                                            label="Model"
                                            onChange={(event) => setSelectedModel(event.target.value)}
                                        >
                                            {MODEL_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedPrompt && selectedPrompt.llmModel === selectedModel && (
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.7rem', ml: 0.5 }}>
                                            (from prompt)
                                        </Typography>
                                    )}
                                </Box>
                                <ToggleButtonGroup
                                    value={enableWebSearch ? 'enabled' : 'disabled'}
                                    exclusive
                                    onChange={(event, newValue) => {
                                        setEnableWebSearch(newValue === 'enabled');
                                    }}
                                    size="small"
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            color: 'text.secondary',
                                            borderColor: 'divider',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            },
                                            '&.Mui-selected': {
                                                color: '#fff',
                                                backgroundColor: '#d32f2f',
                                                borderColor: '#d32f2f',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: '#b71c1c'
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton
                                        value="enabled"
                                        sx={{
                                            fontSize: '0.75rem',
                                            px: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                        title="Enable web search"
                                    >
                                        <SearchIcon fontSize="small" />
                                        Web Search
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={clearConversation}
                                    disabled={conversationDisplay.length === 0 && !response && !userInput}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Stack>

                        {/* Scrollable Content Area - Conversation Display */}
                        <Box sx={{ flexGrow: 1, pr: 1, minHeight: 0, mb: 2, overflow: 'hidden' }}>
                            {/* Response Area */}
                            <Box sx={{
                                height: '100%',
                                p: 2,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                overflow: 'auto',
                                position: 'relative'
                            }}>
                                {/* Expand / Collapse Icon - Positioned in top-right corner */}
                                <Tooltip title={isExpanded ? 'Collapse chat' : 'Expand chat'}>
                                    <IconButton
                                        size="small"
                                        onClick={toggleExpand}
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            zIndex: 10,
                                            backgroundColor: 'background.paper',
                                            boxShadow: 2,
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                                {conversationDisplay.length > 0 || (isLoading && response) ? (
                                    <Box>
                                        {conversationDisplay.map((message, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    mb: 2,
                                                    display: 'flex',
                                                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                                                }}
                                            >
                                                <Paper
                                                    sx={{
                                                        p: 2,
                                                        maxWidth: '75%',
                                                        backgroundColor: 'grey.100',
                                                        color: 'text.primary',
                                                        borderRadius: message.role === 'user'
                                                            ? '18px 18px 4px 18px'
                                                            : '18px 18px 18px 4px'
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                        {message.role === 'user' ? 'You:' : 'Assistant:'}
                                                    </Typography>
                                                    {message.role === 'assistant' ? (
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkGfm]}
                                                            children={message.content}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {message.content}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            </Box>
                                        ))}
                                        {/* Show current streaming response */}
                                        {isLoading && response && (
                                            <Box sx={{
                                                mb: 2,
                                                display: 'flex',
                                                justifyContent: 'flex-start'
                                            }}>
                                                <Paper
                                                    sx={{
                                                        p: 2,
                                                        maxWidth: '75%',
                                                        backgroundColor: 'grey.100',
                                                        borderRadius: '18px 18px 18px 4px'
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                        Assistant:
                                                    </Typography>
                                                    <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        children={response}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Thinking...
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        Conversation will appear here...
                                    </Typography>
                                )}
                            </Box>

                            {/* Error Display */}
                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* File Limit Warning */}
                            {uploadedFiles.length >= MAX_FILES - 1 && uploadedFiles.length < MAX_FILES && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    You're approaching the file limit ({uploadedFiles.length}/{MAX_FILES} files uploaded). Maximum {MAX_FILES} files allowed.
                                </Alert>
                            )}

                            {uploadedFiles.length > MAX_FILES && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    Too many files! Please remove {uploadedFiles.length - MAX_FILES} file{uploadedFiles.length - MAX_FILES !== 1 ? 's' : ''} to continue. Maximum {MAX_FILES} files allowed.
                                </Alert>
                            )}
                        </Box>

                        {/* Input Area - Fixed at bottom */}
                        <Box sx={{ flexShrink: 0 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Enter your message here..."
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                disabled={isLoading}
                                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                    // Enter sends message; Shift+Enter inserts newline
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (!isLoading) {
                                            handleSubmit();
                                        }
                                    }
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        background: 'background.paper',
                                                        borderRadius: '40px',
                                                        boxShadow: 'none',
                                                        height: '40px',
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        cursor: isLoading || (!selectedPromptId && !customPrompt.trim()) || !userInput.trim() || uploadedFiles.length > MAX_FILES ? 'default' : 'pointer',
                                                        px: 0,
                                                        py: 0,
                                                        border: '1.5px solid #e50c20',
                                                        transition: 'background 0.2s, color 0.2s',
                                                        opacity: isLoading || (!selectedPromptId && !customPrompt.trim()) || !userInput.trim() || uploadedFiles.length > MAX_FILES ? 0.5 : 1,
                                                        ...(!(isLoading || (!selectedPromptId && !customPrompt.trim()) || !userInput.trim() || uploadedFiles.length > MAX_FILES) && {
                                                            '&:hover': {
                                                                bgcolor: '#e50c20',
                                                                '& .send-btn-text': {
                                                                    color: '#fff',
                                                                },
                                                            },
                                                        }),
                                                    }}
                                                    onClick={() => {
                                                        if (!(isLoading || (!selectedPromptId && !customPrompt.trim()) || !userInput.trim() || uploadedFiles.length > MAX_FILES)) {
                                                            handleSubmit();
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            background: '#e50c20',
                                                            width: '2%',
                                                            minWidth: '24px',
                                                            height: '100%',
                                                            borderRadius: '40px 0 0 40px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    />
                                                    <Typography
                                                        className="send-btn-text"
                                                        component="span"
                                                        sx={{
                                                            fontSize: '.95rem',
                                                            fontWeight: 600,
                                                            pl: 2,
                                                            pr: 3,
                                                            py: 1.5,
                                                            textAlign: 'left',
                                                            whiteSpace: 'nowrap',
                                                            color: 'text.primary',
                                                            userSelect: 'none',
                                                            transition: 'color 0.2s',
                                                        }}
                                                    >
                                                        {isLoading ? 'Processing...' : uploadedFiles.length > MAX_FILES ? `Remove files (${uploadedFiles.length}/${MAX_FILES})` : 'Send'}
                                                    </Typography>
                                                </Box>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* Loading Backdrop - Shows when waiting for response to begin */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={isWaitingForResponse}
            >
                <LoadingSpinner text="Thinking" size={120} />
            </Backdrop>
        </Box>
    );
};

export default Playground;